import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { isNoindexRoute } from "@/utils/routes";

interface LLMMetaTagsProps {
  title: string;
  description: string;
  url: string;
  language: 'es' | 'en';
  keywords?: string[];
  author?: string;
  /** Solo se emite geo meta cuando la intención de la página es realmente local. */
  geo?: { region: string; placename: string } | null;
}

/**
 * Metadata estándar por página (title, description, robots, canonical, OG,
 * Twitter, author, language). P5.8: se retiraron los meta artificiales
 * "para LLMs" (llm-optimized, ai-friendly, question-answer-format,
 * credenciales, años de experiencia, last-modified sintético).
 */
export const LLMMetaTags = ({
  title,
  description,
  url,
  language,
  keywords = [],
  author = "Luis ROC",
  geo = null,
}: LLMMetaTagsProps) => {

  const { pathname } = useLocation();
  const robotsNoIndex = isNoindexRoute(pathname);

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Standard SEO */}
      <meta name="robots" content={robotsNoIndex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="author" content={author} />
      <meta name="language" content={language === 'es' ? 'Spanish' : 'English'} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}

      {/* Geo solo en páginas con intención local real */}
      {geo && <meta name="geo.region" content={geo.region} />}
      {geo && <meta name="geo.placename" content={geo.placename} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
      <meta property="og:site_name" content="Luis ROC" />

      {/* Twitter/X Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};
